import { useEffect, useMemo, useRef, useState } from 'react';
import { entferneFoto, fotosZu } from '../fotos';
import { erstelleSpracheingabe, spracheVerfuegbar, type Spracheingabe } from '../spracheingabe';
import { erkenneSprachbefehl } from '../sprachbefehle';
import { strukturiere } from '../strukturierung';
import { RAUM_VORSCHLAEGE, ZUSTAND_LABEL, type Mangel, type Protokoll, type Raum, type Zustand } from '../domain/types';
import {
	HakenIcon,
	KameraIcon,
	MikrofonIcon,
	MuellIcon,
	PfeilRechtsIcon,
	RueckgaengigIcon,
	StiftIcon
} from '../icons';
import { FotoGalerie } from './FotoGalerie';

interface RundgangProps {
	protokoll: Protokoll;
	onAendern: (protokoll: Protokoll) => void;
	onWeiter: () => void;
}

interface VerlaufEintrag {
	protokoll: Protokoll;
	beschreibung: string;
}

interface MangelEntwurf {
	raumId: string;
	mangelId: string;
	gegenstand: string;
	beschreibung: string;
	zustand: Zustand;
}

const ZUSTAND_REIHENFOLGE: Zustand[] = ['in_ordnung', 'gebrauchsspuren', 'mangel'];

function schweregrad(raum: Raum): Zustand {
	if (raum.maengel.some((m) => m.zustand === 'mangel')) return 'mangel';
	if (raum.maengel.some((m) => m.zustand === 'gebrauchsspuren')) return 'gebrauchsspuren';
	return 'in_ordnung';
}

function neuerRaum(name: string): Raum {
	return { id: crypto.randomUUID(), name, zustand: 'in_ordnung', maengel: [], fotoIds: [] };
}

function normalisiereRaumname(name: string): string {
	const bereinigt = name.trim();
	return bereinigt ? bereinigt.charAt(0).toUpperCase() + bereinigt.slice(1) : bereinigt;
}

function dateiZuDataUrl(datei: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const leser = new FileReader();
		leser.onload = () => resolve(String(leser.result));
		leser.onerror = () => reject(leser.error);
		leser.readAsDataURL(datei);
	});
}

export function Rundgang({ protokoll, onAendern, onWeiter }: RundgangProps) {
	const [aktiverRaumId, setAktiverRaumId] = useState<string | null>(protokoll.raeume[0]?.id ?? null);
	const [hoertZu, setHoertZu] = useState(false);
	const [zwischenstand, setZwischenstand] = useState('');
	const [manuellerText, setManuellerText] = useState('');
	const [fehler, setFehler] = useState<string | null>(null);
	const [aktionHinweis, setAktionHinweis] = useState<string | null>(null);
	const [wirdVerarbeitet, setWirdVerarbeitet] = useState(0);
	const [verlauf, setVerlauf] = useState<VerlaufEintrag[]>([]);
	const [entwurf, setEntwurf] = useState<MangelEntwurf | null>(null);
	const [loeschMangelId, setLoeschMangelId] = useState<string | null>(null);
	const spracheingabeRef = useRef<Spracheingabe | null>(null);

	const protokollRef = useRef(protokoll);
	const aktiverRaumIdRef = useRef(aktiverRaumId);
	useEffect(() => {
		protokollRef.current = protokoll;
	}, [protokoll]);
	useEffect(() => {
		aktiverRaumIdRef.current = aktiverRaumId;
	}, [aktiverRaumId]);

	const aktiverRaum = useMemo(
		() => protokoll.raeume.find((r) => r.id === aktiverRaumId) ?? null,
		[protokoll.raeume, aktiverRaumId]
	);

	function setzeAktivenRaum(id: string | null) {
		aktiverRaumIdRef.current = id;
		setAktiverRaumId(id);
	}

	function aendereMitVerlauf(neu: Protokoll, beschreibung: string, basis = protokollRef.current) {
		setVerlauf((bisher) => [...bisher, { protokoll: basis, beschreibung }].slice(-10));
		protokollRef.current = neu;
		onAendern(neu);
		setAktionHinweis(beschreibung);
		setFehler(null);
	}

	function rueckgaengig() {
		const letzter = verlauf[verlauf.length - 1];
		if (!letzter) {
			setFehler('Es gibt noch nichts rückgängig zu machen.');
			return;
		}
		setVerlauf((bisher) => bisher.slice(0, -1));
		protokollRef.current = letzter.protokoll;
		onAendern(letzter.protokoll);
		const aktiverExistiert = letzter.protokoll.raeume.some((raum) => raum.id === aktiverRaumIdRef.current);
		if (!aktiverExistiert) setzeAktivenRaum(letzter.protokoll.raeume[0]?.id ?? null);
		setEntwurf(null);
		setLoeschMangelId(null);
		setFehler(null);
		setAktionHinweis(`Rückgängig: ${letzter.beschreibung}`);
	}

	function raumHinzufuegen(name: string, beschreibung = 'Raum hinzugefügt'): string | null {
		const bereinigt = normalisiereRaumname(name);
		if (!bereinigt) return null;
		const aktuellesProtokoll = protokollRef.current;
		const bestehender = aktuellesProtokoll.raeume.find(
			(r) => r.name.toLowerCase() === bereinigt.toLowerCase()
		);
		if (bestehender) {
			setzeAktivenRaum(bestehender.id);
			setAktionHinweis(`${bestehender.name} ausgewählt`);
			return bestehender.id;
		}
		const raum = neuerRaum(bereinigt);
		aendereMitVerlauf(
			{ ...aktuellesProtokoll, raeume: [...aktuellesProtokoll.raeume, raum] },
			beschreibung,
			aktuellesProtokoll
		);
		setzeAktivenRaum(raum.id);
		return raum.id;
	}

	function behandleSprachbefehl(satz: string): boolean {
		const befehl = erkenneSprachbefehl(satz);
		if (!befehl) return false;

		if (befehl.typ === 'rueckgaengig') {
			rueckgaengig();
			return true;
		}
		if (befehl.typ === 'weiter') {
			if (protokollRef.current.raeume.length === 0) {
				setFehler('Vor den Zählern bitte mindestens einen Raum erfassen.');
				return true;
			}
			spracheingabeRef.current?.stop();
			setHoertZu(false);
			onWeiter();
			return true;
		}
		if (befehl.typ === 'raum_wechseln') {
			raumHinzufuegen(befehl.raumName, `Raum „${normalisiereRaumname(befehl.raumName)}“ hinzugefügt`);
			return true;
		}

		const raeume = protokollRef.current.raeume;
		if (raeume.length === 0) {
			setFehler('Es gibt noch keinen nächsten Raum.');
			return true;
		}
		const aktuellerIndex = raeume.findIndex((raum) => raum.id === aktiverRaumIdRef.current);
		const naechster = raeume[aktuellerIndex + 1];
		if (!naechster) {
			setFehler('Das ist bereits der letzte erfasste Raum.');
			return true;
		}
		setzeAktivenRaum(naechster.id);
		setAktionHinweis(`${naechster.name} ausgewählt`);
		return true;
	}

	async function verarbeiteSatz(satz: string) {
		setAktionHinweis(null);
		if (behandleSprachbefehl(satz)) return;

		setWirdVerarbeitet((n) => n + 1);
		let ergebnis;
		try {
			ergebnis = await strukturiere(
				satz,
				protokollRef.current.raeume.map((r) => r.name)
			);
		} finally {
			setWirdVerarbeitet((n) => n - 1);
		}

		const aktuellesProtokoll = protokollRef.current;
		let raeume = aktuellesProtokoll.raeume;
		let raumId = aktiverRaumIdRef.current;

		if (ergebnis.raum) {
			const bestehender = raeume.find((r) => r.name.toLowerCase() === ergebnis.raum!.toLowerCase());
			if (bestehender) {
				raumId = bestehender.id;
			} else {
				const raum = neuerRaum(normalisiereRaumname(ergebnis.raum));
				raeume = [...raeume, raum];
				raumId = raum.id;
			}
		}

		if (ergebnis.mangel && raumId) {
			const mangel: Mangel = {
				id: crypto.randomUUID(),
				gegenstand: ergebnis.mangel.gegenstand,
				beschreibung: ergebnis.mangel.beschreibung,
				zustand: ergebnis.mangel.zustand,
				fotoIds: [],
				quelle: satz
			};
			raeume = raeume.map((r) => (r.id === raumId ? { ...r, maengel: [...r.maengel, mangel] } : r));
		} else if (ergebnis.mangel && !raumId) {
			setFehler('Ich habe einen Befund erkannt, aber noch keinen Raum. Bitte zuerst einen Raum nennen oder auswählen.');
			return;
		}

		if (raeume !== aktuellesProtokoll.raeume) {
			aendereMitVerlauf({ ...aktuellesProtokoll, raeume }, `„${satz}“ erfasst`, aktuellesProtokoll);
		}
		if (raumId !== aktiverRaumIdRef.current) setzeAktivenRaum(raumId);
	}

	function mikrofonUmschalten() {
		if (hoertZu) {
			spracheingabeRef.current?.stop();
			setHoertZu(false);
			setZwischenstand('');
			return;
		}
		setFehler(null);
		setAktionHinweis(null);
		const eingabe = erstelleSpracheingabe({
			onZwischenstand: setZwischenstand,
			onSatz: (satz) => {
				setZwischenstand('');
				void verarbeiteSatz(satz);
			},
			onFehler: (grund) => setFehler(`Spracherkennung: ${grund}`),
			onEnde: () => setHoertZu(false)
		});
		if (!eingabe) {
			setFehler('Spracherkennung wird von diesem Browser nicht unterstützt — bitte unten eintippen.');
			return;
		}
		spracheingabeRef.current = eingabe;
		eingabe.start();
		setHoertZu(true);
	}

	useEffect(() => () => spracheingabeRef.current?.stop(), []);

	function manuellHinzufuegen() {
		const satz = manuellerText.trim();
		if (!satz) return;
		void verarbeiteSatz(satz);
		setManuellerText('');
	}

	function zustandWechseln(raumId: string, mangelId: string) {
		const basis = protokollRef.current;
		const neu = {
			...basis,
			raeume: basis.raeume.map((raum) => {
				if (raum.id !== raumId) return raum;
				return {
					...raum,
					maengel: raum.maengel.map((mangel) => {
						if (mangel.id !== mangelId) return mangel;
						const index = ZUSTAND_REIHENFOLGE.indexOf(mangel.zustand);
						return { ...mangel, zustand: ZUSTAND_REIHENFOLGE[(index + 1) % ZUSTAND_REIHENFOLGE.length] };
					})
				};
			})
		};
		aendereMitVerlauf(neu, 'Zustand geändert', basis);
	}

	function bearbeitungStarten(raumId: string, mangel: Mangel) {
		setEntwurf({
			raumId,
			mangelId: mangel.id,
			gegenstand: mangel.gegenstand,
			beschreibung: mangel.beschreibung,
			zustand: mangel.zustand
		});
		setLoeschMangelId(null);
	}

	function bearbeitungSpeichern() {
		if (!entwurf?.gegenstand.trim() || !entwurf.beschreibung.trim()) return;
		const basis = protokollRef.current;
		const neu = {
			...basis,
			raeume: basis.raeume.map((raum) =>
				raum.id === entwurf.raumId
					? {
							...raum,
							maengel: raum.maengel.map((mangel) =>
								mangel.id === entwurf.mangelId
									? {
											...mangel,
											gegenstand: entwurf.gegenstand.trim(),
											beschreibung: entwurf.beschreibung.trim(),
											zustand: entwurf.zustand
										}
									: mangel
							)
						}
					: raum
			)
		};
		aendereMitVerlauf(neu, 'Befund bearbeitet', basis);
		setEntwurf(null);
	}

	function mangelLoeschen(raumId: string, mangelId: string) {
		const basis = protokollRef.current;
		const neu = {
			...basis,
			raeume: basis.raeume.map((raum) => {
				if (raum.id !== raumId) return raum;
				const mangel = raum.maengel.find((eintrag) => eintrag.id === mangelId);
				return {
					...raum,
					// Fotos bleiben als Raumbelege erhalten, statt beim Löschen
					// des Textbefunds stillschweigend mit zu verschwinden.
					fotoIds: mangel ? Array.from(new Set([...raum.fotoIds, ...mangel.fotoIds])) : raum.fotoIds,
					maengel: raum.maengel.filter((eintrag) => eintrag.id !== mangelId)
				};
			})
		};
		aendereMitVerlauf(neu, 'Befund gelöscht', basis);
		setLoeschMangelId(null);
		setEntwurf(null);
	}

	async function fotoAufnehmen(raumId: string, mangelId: string | null, datei: File | undefined) {
		if (!datei) return;
		const datenUrl = await dateiZuDataUrl(datei);
		const basis = protokollRef.current;
		const fotoId = crypto.randomUUID();
		const foto = { id: fotoId, datenUrl, aufgenommenAm: new Date().toISOString() };
		const neu = {
			...basis,
			fotos: [...basis.fotos, foto],
			raeume: basis.raeume.map((raum) => {
				if (raum.id !== raumId) return raum;
				if (mangelId) {
					return {
						...raum,
						maengel: raum.maengel.map((mangel) =>
							mangel.id === mangelId ? { ...mangel, fotoIds: [...mangel.fotoIds, fotoId] } : mangel
						)
					};
				}
				return { ...raum, fotoIds: [...raum.fotoIds, fotoId] };
			})
		};
		aendereMitVerlauf(neu, 'Foto hinzugefügt', basis);
	}

	function fotoLoeschen(fotoId: string) {
		const basis = protokollRef.current;
		aendereMitVerlauf(entferneFoto(basis, fotoId), 'Foto gelöscht', basis);
	}

	const vorschlaege = RAUM_VORSCHLAEGE.filter(
		(name) => !protokoll.raeume.some((r) => r.name.toLowerCase() === name.toLowerCase())
	);
	const letzterVerlauf = verlauf[verlauf.length - 1];
	const aktiverRaumFotoAnzahl = aktiverRaum
		? aktiverRaum.fotoIds.length + aktiverRaum.maengel.reduce((summe, mangel) => summe + mangel.fotoIds.length, 0)
		: 0;

	return (
		<div className="schritt schritt--rundgang">
			<div className="raum-leiste">
				{protokoll.raeume.map((raum) => (
					<button
						key={raum.id}
						type="button"
						className={`raum-chip ${raum.id === aktiverRaumId ? 'ist-aktiv' : ''} raum-chip--${schweregrad(raum)}`}
						onClick={() => setzeAktivenRaum(raum.id)}
					>
						{raum.name}
						{raum.maengel.length > 0 && <span>{raum.maengel.length}</span>}
					</button>
				))}
				{vorschlaege.slice(0, 4).map((name) => (
					<button key={name} type="button" className="raum-chip raum-chip--vorschlag" onClick={() => raumHinzufuegen(name)}>
						+ {name}
					</button>
				))}
			</div>

			<div className="mikrofon-bereich">
				<div className="mikrofon-kopf">
					<div>
						<span className="mikrofon-uebertitel">Sprachaufnahme</span>
						<strong>{aktiverRaum ? aktiverRaum.name : 'Raum einfach mitsprechen'}</strong>
					</div>
					<button
						type="button"
						className="nebenknopf nebenknopf--kompakt"
						onClick={rueckgaengig}
						disabled={!letzterVerlauf}
						aria-label={letzterVerlauf ? `${letzterVerlauf.beschreibung} rückgängig machen` : 'Nichts rückgängig zu machen'}
					>
						<RueckgaengigIcon width={16} height={16} />
						Rückgängig
					</button>
				</div>
				<button
					type="button"
					className={`mikrofon-knopf ${hoertZu ? 'hoert-zu' : ''}`}
					onClick={mikrofonUmschalten}
					disabled={!spracheVerfuegbar() && !hoertZu}
					aria-label={hoertZu ? 'Spracherkennung stoppen' : 'Spracherkennung starten'}
				>
					<span className="mikrofon-symbol">
						<MikrofonIcon width={24} height={24} />
					</span>
					<span>
						<strong>{hoertZu ? 'Ich höre zu …' : 'Befund sprechen'}</strong>
						<small>{hoertZu ? 'Zum Stoppen antippen' : 'Raum, Gegenstand und Zustand frei formulieren'}</small>
					</span>
				</button>
				{zwischenstand && <p className="zwischenstand">„{zwischenstand}“</p>}
				{wirdVerarbeitet > 0 && <p className="verarbeitung-hinweis">Wird strukturiert …</p>}
				{aktionHinweis && (
					<p className="hinweis hinweis--erfolg">
						<HakenIcon width={14} height={14} /> {aktionHinweis}
					</p>
				)}
				{fehler && <p className="hinweis hinweis--fehler">{fehler}</p>}

				<div className="manuell-eingabe">
					<input
						value={manuellerText}
						onChange={(e) => setManuellerText(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && manuellHinzufuegen()}
						placeholder="Oder tippen: Küche, Fenstergriff locker"
						aria-label="Befund manuell eingeben"
					/>
					<button type="button" onClick={manuellHinzufuegen} disabled={!manuellerText.trim()}>
						Hinzufügen
					</button>
				</div>

				<details className="sprachbefehle">
					<summary>Sprachbefehle</summary>
					<p>„Nächster Raum“ · „Wechsle zu Küche“ · „Rückgängig“ · „Weiter zu den Zählern“</p>
				</details>
			</div>

			{aktiverRaum ? (
				<div className="befunde">
					<div className="befunde-kopf">
						<div>
							<h2>{aktiverRaum.name}</h2>
							<span>
								{aktiverRaum.maengel.length} {aktiverRaum.maengel.length === 1 ? 'Befund' : 'Befunde'} ·{' '}
								{aktiverRaumFotoAnzahl} {aktiverRaumFotoAnzahl === 1 ? 'Foto' : 'Fotos'}
							</span>
						</div>
						<label className="foto-knopf">
							<KameraIcon width={16} height={16} />
							Raumfoto
							<input
								type="file"
								accept="image/*"
								capture="environment"
								onChange={(e) => {
									void fotoAufnehmen(aktiverRaum.id, null, e.target.files?.[0]);
									e.target.value = '';
								}}
							/>
						</label>
					</div>

					<FotoGalerie
						fotos={fotosZu(protokoll, aktiverRaum.fotoIds)}
						label={`Raumfotos ${aktiverRaum.name}`}
						onEntfernen={fotoLoeschen}
					/>

					{aktiverRaum.maengel.length === 0 && (
						<div className="leerzustand">
							<MikrofonIcon width={22} height={22} />
							<strong>Noch keine Befunde</strong>
							<span>Einfach drauflos sprechen oder oben eintippen.</span>
						</div>
					)}

					<ul className="befund-liste">
						{aktiverRaum.maengel.map((mangel) => {
							const wirdBearbeitet = entwurf?.mangelId === mangel.id;
							return (
								<li key={mangel.id} className={`befund-eintrag ${wirdBearbeitet ? 'ist-in-bearbeitung' : ''}`}>
									{wirdBearbeitet && entwurf ? (
										<div className="befund-formular">
											<label>
												<span>Gegenstand</span>
												<input
													value={entwurf.gegenstand}
													onChange={(e) => setEntwurf({ ...entwurf, gegenstand: e.target.value })}
												/>
											</label>
											<label>
												<span>Beschreibung</span>
												<textarea
													value={entwurf.beschreibung}
													onChange={(e) => setEntwurf({ ...entwurf, beschreibung: e.target.value })}
												/>
											</label>
											<label>
												<span>Zustand</span>
												<select
													value={entwurf.zustand}
													onChange={(e) => setEntwurf({ ...entwurf, zustand: e.target.value as Zustand })}
												>
													{ZUSTAND_REIHENFOLGE.map((zustand) => (
														<option key={zustand} value={zustand}>
															{ZUSTAND_LABEL[zustand]}
														</option>
													))}
												</select>
											</label>
											<div className="befund-formular-aktionen">
												<button type="button" className="nebenknopf" onClick={() => setEntwurf(null)}>
													Abbrechen
												</button>
												<button
													type="button"
													className="hauptknopf hauptknopf--klein"
													onClick={bearbeitungSpeichern}
													disabled={!entwurf.gegenstand.trim() || !entwurf.beschreibung.trim()}
												>
													Speichern
												</button>
											</div>
										</div>
									) : (
										<>
											<div className="befund-eintrag-kopf">
												<button
													type="button"
													className={`zustand-abzeichen zustand-abzeichen--${mangel.zustand}`}
													onClick={() => zustandWechseln(aktiverRaum.id, mangel.id)}
													title="Zustand ändern"
												>
													{ZUSTAND_LABEL[mangel.zustand]}
												</button>
												<div className="befund-aktionen">
													<button
														type="button"
														className="icon-knopf"
														onClick={() => bearbeitungStarten(aktiverRaum.id, mangel)}
														aria-label={`${mangel.gegenstand} bearbeiten`}
													>
														<StiftIcon width={15} height={15} />
													</button>
													<button
														type="button"
														className="icon-knopf"
														onClick={() => setLoeschMangelId(mangel.id)}
														aria-label={`${mangel.gegenstand} löschen`}
													>
														<MuellIcon width={15} height={15} />
													</button>
												</div>
											</div>
											<div className="befund-text">
												<strong>{mangel.gegenstand}</strong>
												<span>{mangel.beschreibung}</span>
											</div>
											{mangel.quelle && (
												<details className="befund-quelle">
													<summary>Originalsatz anzeigen</summary>
													<p>„{mangel.quelle}“</p>
												</details>
											)}
											{loeschMangelId === mangel.id && (
												<div className="befund-loeschfrage" role="alert">
													<span>Befund löschen? Zugehörige Fotos bleiben als Raumfotos erhalten.</span>
													<div>
														<button type="button" className="nebenknopf" onClick={() => setLoeschMangelId(null)}>
															Abbrechen
														</button>
														<button
															type="button"
															className="nebenknopf nebenknopf--gefahr"
															onClick={() => mangelLoeschen(aktiverRaum.id, mangel.id)}
														>
															Löschen
														</button>
													</div>
												</div>
											)}
											<div className="befund-fotozeile">
												<label className="foto-knopf foto-knopf--klein">
													<KameraIcon width={14} height={14} />
													Beweisfoto
													<input
														type="file"
														accept="image/*"
														capture="environment"
														onChange={(e) => {
															void fotoAufnehmen(aktiverRaum.id, mangel.id, e.target.files?.[0]);
															e.target.value = '';
														}}
													/>
												</label>
												<span>
													{mangel.fotoIds.length === 0
														? 'Noch kein Foto'
														: `${mangel.fotoIds.length} ${mangel.fotoIds.length === 1 ? 'Foto' : 'Fotos'}`}
												</span>
											</div>
											<FotoGalerie
												fotos={fotosZu(protokoll, mangel.fotoIds)}
												label={`Beweisfotos ${mangel.gegenstand}`}
												onEntfernen={fotoLoeschen}
											/>
										</>
									)}
								</li>
							);
						})}
					</ul>
				</div>
			) : (
				<div className="leerzustand">
					<strong>Ersten Raum wählen</strong>
					<span>Oben antippen oder beispielsweise „Wechsle zu Küche“ sagen.</span>
				</div>
			)}

			<footer className="schritt-fuss">
				<button
					type="button"
					className="hauptknopf hauptknopf--mit-icon"
					onClick={onWeiter}
					disabled={protokoll.raeume.length === 0}
				>
					Weiter
					<PfeilRechtsIcon width={20} height={20} />
				</button>
			</footer>
		</div>
	);
}
